/**
 * Pure builders for the Today "daily companion" view: a merged day timeline and
 * the calorie energy-balance readout. DOM-free so ordering and formatting are
 * unit-tested directly.
 */
import type { MetricPoint, StoredMeal, Workout } from '../api.js';
import { fmtDuration, fmtNumber } from './format.js';

export interface TimelineEvent {
  at: string;
  kind: 'meal' | 'workout' | 'caffeine' | 'water' | 'work';
  title: string;
  detail: string | null;
}

interface TimelineInput {
  meals: StoredMeal[];
  workouts: Workout[];
  caffeine: MetricPoint[];
  water: MetricPoint[];
  workSessions: Array<{ date: string; hours: number }>;
}

/** "520 kcal · 32 g protein", omitting whichever total is absent; null if both. */
function mealDetail(meal: StoredMeal): string | null {
  let cal = 0;
  let protein = 0;
  for (const it of meal.items) {
    cal += it.calories ?? 0;
    protein += it.protein_g ?? 0;
  }
  const parts: string[] = [];
  if (cal > 0) parts.push(`${fmtNumber(cal, 0)} kcal`);
  if (protein > 0) parts.push(`${fmtNumber(protein, 0)} g protein`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * Merge every logged thing in the day into one ascending, stable timeline.
 * Work maps to a single event at its (bare) session date; the metric-backed
 * kinds (caffeine, water) carry the point value in their detail.
 */
export function buildDayTimeline(input: TimelineInput): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  for (const m of input.meals) {
    events.push({ at: m.eatenAt, kind: 'meal', title: m.name ?? 'Meal', detail: mealDetail(m) });
  }
  for (const w of input.workouts) {
    if (!w.startedAt) continue;
    events.push({
      at: w.startedAt,
      kind: 'workout',
      title: w.type ?? 'Workout',
      detail: w.durationSec ? fmtDuration(w.durationSec) : null,
    });
  }
  for (const p of input.caffeine) {
    events.push({ at: p.t, kind: 'caffeine', title: 'Caffeine', detail: `${fmtNumber(p.v, 0)} mg` });
  }
  for (const p of input.water) {
    events.push({ at: p.t, kind: 'water', title: 'Water', detail: `${fmtNumber(p.v, 0)} mL` });
  }
  for (const s of input.workSessions) {
    events.push({ at: s.date, kind: 'work', title: 'Work', detail: `${fmtNumber(s.hours)} h` });
  }

  // Decorate-sort keeps equal-`at` events in insertion order (stable).
  return events
    .map((e, i) => ({ e, i }))
    .sort((a, b) => (a.e.at < b.e.at ? -1 : a.e.at > b.e.at ? 1 : a.i - b.i))
    .map(({ e }) => e);
}

export interface EnergyBalance {
  in: number;
  out: number;
  net: number;
}

/** Calories in vs out (basal + active); net is in − out. Missing parts count as 0. */
export function energyBalance(input: { caloriesIn: number; active: number; basal: number }): EnergyBalance {
  const inKcal = Number.isFinite(input.caloriesIn) ? input.caloriesIn : 0;
  const active = Number.isFinite(input.active) ? input.active : 0;
  const basal = Number.isFinite(input.basal) ? input.basal : 0;
  const out = basal + active;
  return { in: inKcal, out, net: inKcal - out };
}
